/* eslint-disable complexity */
import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import Button, { LinkButton } from '../../components/buttons/Button';
import './CreateUser.scss';
import { settings } from '../../settings';
import Fetch from '../../utils/fetch';

import { Loader } from '../../components/Loader/Loader';
import { RouteComponentProps } from 'react-router-dom';
import { Account, CourierTravelMode, Role, User } from '../../types/userTypes';
import { FaChargingStation, FaGasPump, FaOilCan, FaPlug } from 'react-icons/fa';
import { ReactComponent as BikeIcon } from '../../assets/newsvg/bike.svg';
import { ReactComponent as CargoBikeIcon } from '../../assets/newsvg/bike-cargo.svg';
import { fetchAccounts } from '../../fetch/fetchUsers';
import { CarType } from '../../types/bundleTypes';
import {
  Region,
  regionNames,
  RegionsList,
  ServiceBrand,
} from '../../types/orderTypes';
import { classNames } from '../../utils/classUtils';
import { toggleElement } from '../../utils/array';
import { capitalize } from '../../utils/strings';
import { ErrorMsg } from '../users/EditAdminUserModal';
import { IsThereAnyErrors, validateNewUser } from '../users/validateFields';
import {
  fetchCompanyName,
  getOrgNumberStringAsString,
} from '../../utils/enhetsregisteretUtil';
import { ReqEnum } from '../../const/requestStatus';
import { UsersMenu } from '../users/UsersMenu';
import {
  getDialingCode,
  isOrganisationNumberValid,
  supportedCountries,
} from '../../utils/country';
import clsx from 'clsx';
import Alert from '../../components/alerts/Alert';

export function ShowTestAccountIfItExists({
  isCompanyInTestAccountsList,
  isCompanyInAccountsList,
  isIdenticalCompanyNumber,
  role,
}: {
  isCompanyInTestAccountsList: boolean;
  isCompanyInAccountsList: boolean;
  isIdenticalCompanyNumber: boolean;
  role: Role;
}): JSX.Element {
  if (
    role === 'COURIER' &&
    isCompanyInAccountsList &&
    !isIdenticalCompanyNumber
  ) {
    return (
      <em
        style={{
          fontWeight: 500,
          color: 'grey',
          marginLeft: '10px',
        }}
      >
        {`Note: This ${
          isCompanyInTestAccountsList ? 'test' : ''
        } account already exists (there are other users connected to that account). The user will be linked to the existing one.`}
      </em>
    );
  } else if (role === 'COURIER' && isIdenticalCompanyNumber) {
    return (
      <em
        style={{
          fontWeight: 500,
          color: 'grey',
          marginLeft: '10px',
        }}
      >
        {`Note: This ${
          isCompanyInTestAccountsList ? 'test' : ''
        } account is the current account of the courier. Nothing will change.`}
      </em>
    );
  } else return <> </>;
}

export function filterAccountsBasedOnUserType(
  user: Partial<User>,
  acc: Account
): boolean {
  if (!user) {
    return false;
  } else if (user.role === 'SENDER' || user.role === 'SENDER_ADMIN') {
    return acc.category === 'SENDER';
  } else if (user.role === 'COURIER') {
    return acc.category === 'COURIER';
  } else {
    return false;
  }
}

export function filterAccountsBasedOnCountry(
  user: Partial<User>,
  acc: Account
): boolean {
  if (!user) {
    return false;
  } else {
    return (
      supportedCountries[user.country.toUpperCase()].countryCode ===
      acc.countryCode
    );
  }
}

export function IsCompanyInTestAccountsList(
  user: Partial<User>,
  currentOrgNumber: string,
  accounts: Account[]
): boolean {
  return (
    currentOrgNumber.startsWith('00000') &&
    currentOrgNumber.substring(5, 9) !== '0000' &&
    accounts
      .filter(acc => filterAccountsBasedOnUserType(user, acc))
      .filter(acc => filterAccountsBasedOnCountry(user, acc))
      .filter(obj => obj.companyNumber === currentOrgNumber).length > 0
  );
}

export function IsCompanyInAccountsList(
  user: Partial<User>,
  currentOrgNumber: string,
  accounts: Account[]
): boolean {
  return (
    accounts
      .filter(acc => filterAccountsBasedOnUserType(user, acc))
      .filter(acc => filterAccountsBasedOnCountry(user, acc))
      .filter(obj => obj.companyNumber === currentOrgNumber).length > 0
  );
}

export function AccountsChooseOrCreateRadioButtons({
  classNameSpan = '',
  classNameDiv = '',
  checkedUseExistingAccount,
  checkedCreateNewAccount,
  onChangeChooseExistingAccount,
  onChangeChooseCreateNewAccount,
  disabled = false,
}: {
  classNameSpan?: string;
  classNameDiv?: string;
  checkedUseExistingAccount: boolean;
  checkedCreateNewAccount: boolean;
  onChangeChooseExistingAccount: React.ChangeEventHandler;
  onChangeChooseCreateNewAccount: React.ChangeEventHandler;
  disabled?: boolean;
}): JSX.Element {
  return (
    <label>
      <span className={classNameSpan}>Account</span>
      <div className={classNameDiv}>
        <label>
          <input
            disabled={disabled}
            type="radio"
            name={'kindOfAccount'}
            onChange={onChangeChooseExistingAccount}
            checked={checkedUseExistingAccount}
          />
          Use existing account
        </label>
        <label>
          <input
            disabled={disabled}
            type="radio"
            name={'kindOfAccount'}
            onChange={onChangeChooseCreateNewAccount}
            checked={checkedCreateNewAccount}
          />
          Create new account
        </label>
      </div>
    </label>
  );
}

export interface NewUser {
  role: Role;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneCode: string;
  phoneNumber: string;
  companyName?: string;
  companyNumber?: string;
  country?: string;
  paymentByInvoiceAllowed: boolean;
  isCourier: boolean;
  isSorter: boolean;
  travelMode: CourierTravelMode;
  carType?: CarType;
  plateNumbers?: string[];
  accountId?: number;
  regions: Region[];
  limitedAccess: boolean;
  brandPreset?: ServiceBrand;
}

interface CreateResult {
  success: boolean;
  errors: ErrorMsg[];
}

const CreateNewAccountId = -1;
const ChooseAccount = -2;

interface State {
  user: NewUser;
  userCreated: boolean;
  creatingUser: boolean;
  fieldErrors: {
    [key: string]: ErrorMsg;
  };
  errors: ErrorMsg[];
  accounts: Account[];
  accountIdSelected?: number;

  reqCompanyNameFromFetch: ReqEnum;
  companyNameFromFetch: string;
  isCompanyInTestAccountsList: boolean;
  isCompanyInAccountsList: boolean;
  sortByOrgNumber: boolean;
}

export default class CreateUser extends Component<RouteComponentProps, State> {
  constructor(props: null) {
    super(props);

    this.state = {
      userCreated: false,
      creatingUser: false,
      user: {
        role: 'SENDER',
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        phoneCode: '+47',
        phoneNumber: '',
        companyName: '',
        companyNumber: '',
        country: 'Norway',
        paymentByInvoiceAllowed: false,
        isCourier: false,
        isSorter: false,
        travelMode: CourierTravelMode.Bike,
        carType: CarType.NORMAL_BIKE,
        plateNumbers: [],
        accountId: null,
        regions: [],
        limitedAccess: false,
        brandPreset: ServiceBrand.PORTERBUDDY,
      },
      fieldErrors: {},
      errors: [],
      accounts: [],
      accountIdSelected: ChooseAccount,
      companyNameFromFetch: '',
      reqCompanyNameFromFetch: ReqEnum.PENDING,
      isCompanyInTestAccountsList: false,
      isCompanyInAccountsList: false,
      sortByOrgNumber: false,
    };
  }

  async componentDidMount(): Promise<void | undefined> {
    const accounts: Account[] = ((await fetchAccounts()) || []).filter(
      acc => !!acc.name && acc.enabled
    );
    this.setState({
      accounts: accounts,
    });
  }

  createNewAccountSelected(): boolean {
    return this.state.accountIdSelected === CreateNewAccountId;
  }

  chooseAccountSelected(): boolean {
    return this.state.accountIdSelected === ChooseAccount;
  }

  updateUser = (updatedUserFields: Partial<NewUser>): void => {
    const { user } = this.state;
    const updatedUser = {
      ...user,
      ...updatedUserFields,
    };
    this.setState({
      user: updatedUser,
      userCreated: false,
    });
    if (IsThereAnyErrors(this.state.fieldErrors)) {
      const fieldErrors = validateNewUser(updatedUser, CreateNewAccountId);
      this.setState({
        fieldErrors: fieldErrors,
      });
    }
  };

  async createUser(e: React.MouseEvent<Element, MouseEvent>): Promise<void> {
    e.preventDefault();
    const { user } = this.state;
    const fieldErrors = validateNewUser(user, CreateNewAccountId);
    this.setState({
      fieldErrors: fieldErrors,
    });
    if (IsThereAnyErrors(fieldErrors)) {
      return;
    }

    this.setState({
      userCreated: false,
      errors: [],
      creatingUser: true,
    });
    const result = await this.postCreateUser(user);

    if (result.success) {
      this.setState({
        userCreated: true,
        errors: [],
        creatingUser: false,
      });
    } else {
      this.setState({
        userCreated: false,
        errors: result.errors,
        creatingUser: false,
      });
    }
  }
  async getCompanyName(orgnumberAsString: string): Promise<void> {
    const currentOrgNumber: string =
      getOrgNumberStringAsString(orgnumberAsString);

    this.setState({ reqCompanyNameFromFetch: ReqEnum.PENDING });
    if (isOrganisationNumberValid(currentOrgNumber, this.state.user.country)) {
      const { companyNameFromFetch, status } = await fetchCompanyName(
        currentOrgNumber,
        this.state.user.country
      );

      if (
        companyNameFromFetch &&
        companyNameFromFetch.length > 0 &&
        status.toString() !== '404'
      ) {
        this.setState({ companyNameFromFetch: companyNameFromFetch });
        this.setState({ reqCompanyNameFromFetch: ReqEnum.SUCCESS });

        this.setState({
          isCompanyInTestAccountsList: IsCompanyInTestAccountsList(
            this.state.user,
            currentOrgNumber,
            this.state.accounts
          ),
          isCompanyInAccountsList: IsCompanyInAccountsList(
            this.state.user,
            currentOrgNumber,
            this.state.accounts
          ),
        });
      } else if (
        companyNameFromFetch &&
        companyNameFromFetch.length > 0 &&
        status.toString() === '500'
      )
        this.setState({ reqCompanyNameFromFetch: ReqEnum.ERROR });
      else {
        this.setState({ reqCompanyNameFromFetch: ReqEnum.PENDING });
      }
    } else {
      this.setState({ reqCompanyNameFromFetch: ReqEnum.PENDING });
      this.setState({
        isCompanyInTestAccountsList: false,
        isCompanyInAccountsList: false,
      });
    }
  }

  async postCreateUser(user: NewUser): Promise<CreateResult> {
    try {
      let path: string;
      if (user.role === 'ADMIN') {
        path = 'admin';
      } else if (user.role === 'COURIER') {
        path = 'courier';
      } else if (user.role === 'SENDER') {
        path = 'create-sender';
      } else if (user.role === 'SENDER_ADMIN') {
        path = 'create-sender-admin';
      } else {
        return {
          success: false,
          errors: [
            {
              message: `Unexpected role: ${user.role}`,
            },
          ],
        };
      }

      const res = await Fetch.post({
        url: `${settings.newApiUrl}/user/${path}`,
        body: {
          email: user.email,
          password: user.password,
          firstName: user.firstName,
          lastName: user.lastName,
          phoneCode: user.phoneCode,
          phoneNumber: user.phoneNumber,
          companyName: user.companyName,
          companyNumber: user.companyNumber,
          country: user.country,
          paymentByInvoiceAllowed: user.paymentByInvoiceAllowed,
          isCourier: user.isCourier,
          isSorter: user.isSorter,
          travelMode: user.travelMode,
          carType: user.carType,
          plateNumbers: user.plateNumbers || [],
          accountId: user.accountId,
          regions: user.regions || [],
          limitedAccess: user.limitedAccess,
          createAccount: user.accountId === -1,
          brandPreset: user.brandPreset,
        },
      });
      if (res.ok) {
        return { success: true, errors: [] };
      } else if (res.status === 422) {
        return {
          success: false,
          errors: (await res.json()).map(msg => ({ message: msg.message })),
        };
      } else {
        return {
          success: false,
          errors: [
            {
              message: 'Something went wrong. The user could not be created',
            },
          ],
        };
      }
    } catch (e) {
      return {
        success: false,
        errors: [
          {
            message: 'Something went wrong. The user could not be created',
          },
        ],
      };
    }
  }

  private onAccountSelected(value: string) {
    const accountIdSelected = value ? parseInt(value) : null;
    this.updateUser({ accountId: accountIdSelected });
    this.setState({ accountIdSelected: accountIdSelected });
  }
  render(): JSX.Element {
    const {
      user,
      errors,
      fieldErrors,
      userCreated,
      creatingUser,
      accounts,
      accountIdSelected,
    } = this.state;
    const isSender = user.role === 'SENDER' || user.role === 'SENDER_ADMIN';
    const isCourier = user.role === 'COURIER';
    const isAdmin = user.role === 'ADMIN';
    const account = accountIdSelected
      ? this.state.accounts.find(acc => acc.id === accountIdSelected)
      : null;

    return (
      <>
        <Helmet>
          <title>Users - Create User</title>
        </Helmet>
        <UsersMenu />
        <section className="create-user">
          <h3>Create new user</h3>
          <form className="create-user__form">
            <div className="create-user__form-group">
              <label>
                <span>Role</span>
                <select
                  onChange={event => {
                    this.setState({
                      accountIdSelected: ChooseAccount,
                      companyNameFromFetch: '',
                      reqCompanyNameFromFetch: ReqEnum.PENDING,
                      isCompanyInAccountsList: false,
                      isCompanyInTestAccountsList: false,
                    });
                    this.updateUser({
                      companyName: '',
                      companyNumber: '',
                      country: 'Norway',
                      role: event.target.value as Role,
                      travelMode: CourierTravelMode.Bike,
                      carType: CarType.NORMAL_BIKE,
                    });
                  }}
                  value={user.role}
                  disabled={userCreated || creatingUser}
                >
                  <option value="ADMIN">Admin</option>
                  <option value="COURIER">Courier</option>
                  <option value="SENDER">Sender</option>
                  <option value="SENDER_ADMIN">Sender Admin</option>
                </select>
              </label>
              {this.state.user.role === 'COURIER' && (
                <Alert type="warning" className="create-user__warning">
                  Courier users should be created in Dipps, otherwise invoicing
                  will fail.
                </Alert>
              )}
            </div>
            <div
              className={`create-user__form-group ${
                fieldErrors.email ? 'has-error' : ''
              }`}
            >
              <label>
                <span>Email</span>
                <input
                  type="email"
                  placeholder="example@porterbuddy.com"
                  name="email"
                  onChange={event =>
                    this.updateUser({ email: event.target.value })
                  }
                  disabled={userCreated || creatingUser}
                />
              </label>
              {fieldErrors.email && <em>{fieldErrors.email.message}</em>}
            </div>
            <div
              className={`create-user__form-group ${
                fieldErrors.password ? 'has-error' : ''
              }`}
            >
              <label>
                <span>Password</span>
                <input
                  type="password"
                  placeholder="********"
                  name="password"
                  onChange={event =>
                    this.updateUser({ password: event.target.value })
                  }
                  disabled={userCreated || creatingUser}
                  autoComplete="new-password"
                />
              </label>
              {fieldErrors.password && <em>{fieldErrors.password.message}</em>}
            </div>
            <div
              className={`create-user__form-group ${
                fieldErrors.firstName ? 'has-error' : ''
              }`}
            >
              <label>
                <span>First name</span>
                <input
                  type="text"
                  placeholder="Name"
                  name="firstName"
                  onChange={event =>
                    this.updateUser({ firstName: event.target.value })
                  }
                  disabled={userCreated || creatingUser}
                />
              </label>
              {fieldErrors.firstName && (
                <em>{fieldErrors.firstName.message}</em>
              )}
            </div>
            <div
              className={`create-user__form-group ${
                fieldErrors.lastName ? 'has-error' : ''
              }`}
            >
              <label>
                <span>Last name</span>
                <input
                  type="text"
                  placeholder="Last name"
                  name="lastName"
                  onChange={event =>
                    this.updateUser({ lastName: event.target.value })
                  }
                  disabled={userCreated || creatingUser}
                />
              </label>
              {fieldErrors.lastName && <em>{fieldErrors.lastName.message}</em>}
            </div>
            <div
              className={`create-user__form-group ${
                fieldErrors.phoneCode ? 'has-error' : ''
              }`}
            >
              <label>
                <span>Phone country code</span>
                <input
                  type="tel"
                  placeholder="+47"
                  name="phoneCode"
                  value={user.phoneCode}
                  onChange={event =>
                    this.updateUser({ phoneCode: event.target.value })
                  }
                  disabled={userCreated || creatingUser}
                />
              </label>
              {fieldErrors.phoneCode && (
                <em>{fieldErrors.phoneCode.message}</em>
              )}
            </div>
            <div
              className={`create-user__form-group ${
                fieldErrors.phoneNumber ? 'has-error' : ''
              }`}
            >
              <label>
                <span>Phone number</span>
                <input
                  type="tel"
                  placeholder=""
                  name="phoneNumber"
                  onChange={event =>
                    this.updateUser({ phoneNumber: event.target.value })
                  }
                  disabled={userCreated || creatingUser}
                />
              </label>
              {fieldErrors.phoneNumber && (
                <em>{fieldErrors.phoneNumber.message}</em>
              )}
            </div>
            {(isCourier || isSender) && (
              <>
                <div className="create-user__form-group">
                  <label>
                    <span>Country</span>
                    <select
                      onChange={event => {
                        this.setState({
                          accountIdSelected: ChooseAccount,
                          companyNameFromFetch: '',
                          reqCompanyNameFromFetch: ReqEnum.PENDING,
                          isCompanyInAccountsList: false,
                          isCompanyInTestAccountsList: false,
                        });
                        this.updateUser({
                          companyName: '',
                          companyNumber: '',
                          country: event.target.value as string,
                          phoneCode: getDialingCode(
                            event.target.value as string
                          ),
                        });
                      }}
                      value={user.country}
                      disabled={userCreated || creatingUser}
                    >
                      {Object.keys(supportedCountries).map(countryName => (
                        <option key={countryName}>
                          {supportedCountries[countryName].name}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                {this.state.user.country === 'Sweden' && (
                  <div className={'create-user__form-group'}>
                    <label>
                      <span>Help</span>
                      <a
                        href="https://snr4.bolagsverket.se/snrgate/startIn.do"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        External search Swedish org number, use pattern with
                        hyphen, e.g. "556091-2148"
                      </a>
                    </label>
                  </div>
                )}
                <div className={'create-user__form-group'}>
                  <AccountsChooseOrCreateRadioButtons
                    classNameDiv={'create-user__form-radio normal'}
                    checkedUseExistingAccount={
                      accountIdSelected === ChooseAccount ||
                      (accountIdSelected != null && accountIdSelected >= 0)
                    }
                    checkedCreateNewAccount={
                      accountIdSelected === CreateNewAccountId
                    }
                    onChangeChooseExistingAccount={() =>
                      this.onAccountSelected(ChooseAccount.toString())
                    }
                    onChangeChooseCreateNewAccount={() =>
                      this.onAccountSelected(CreateNewAccountId.toString())
                    }
                  />
                </div>
              </>
            )}
            {(this.chooseAccountSelected() ||
              (accountIdSelected != null && accountIdSelected >= 0)) && (
              <>
                <div
                  className={`create-user__form-group ${
                    fieldErrors.account ? 'has-error' : ''
                  } ${isAdmin ? 'hidden-field' : ''}`}
                >
                  <label>
                    <span>Sorting</span>
                    <div className={'create-user__form-radio normal'}>
                      <label>
                        <input
                          type="checkbox"
                          onChange={event => {
                            const previousValue = this.state.sortByOrgNumber;
                            this.setState({ sortByOrgNumber: !previousValue });
                          }}
                        />
                        By organisation number
                      </label>
                    </div>
                  </label>
                  <label>
                    <span>Account</span>
                    <select
                      onChange={e => this.onAccountSelected(e.target.value)}
                      value={
                        accountIdSelected ? accountIdSelected.toString() : ''
                      }
                    >
                      <option value={ChooseAccount.toString()}>
                        {'< Select an account >'}{' '}
                      </option>
                      {accounts
                        .filter(acc =>
                          filterAccountsBasedOnUserType(this.state.user, acc)
                        )
                        .filter(acc =>
                          filterAccountsBasedOnCountry(this.state.user, acc)
                        )
                        .sort((a: Account, b: Account) => {
                          if (this.state.sortByOrgNumber) {
                            if (a.companyNumber < b.companyNumber) {
                              return -1;
                            } else if (a.companyNumber === b.companyNumber) {
                              return 0;
                            } else {
                              return 1;
                            }
                          }
                          return 1;
                        })
                        .map(account => (
                          <option
                            key={`create-user-account-${account.id}`}
                            value={account.id}
                          >
                            {this.state.sortByOrgNumber
                              ? `${account.companyNumber} - ${account.name}`
                              : `${account.name} — ${account.email}`}
                          </option>
                        ))}
                    </select>
                  </label>
                  {fieldErrors.account && (
                    <em>{fieldErrors.account.message}</em>
                  )}
                </div>
              </>
            )}

            {accountIdSelected != null && accountIdSelected >= 0 && (
              <>
                <div
                  className={`create-user__form-group ${
                    fieldErrors.companyName ? 'has-error' : ''
                  } ${isAdmin ? 'hidden-field' : ''}`}
                >
                  <label>
                    <span>Company name</span>

                    <input
                      type="text"
                      placeholder=""
                      name="companyName"
                      readOnly={true}
                      value={
                        !this.createNewAccountSelected() && account
                          ? account.name
                          : user.companyName
                      }
                      disabled={true}
                    />
                  </label>
                  {fieldErrors.companyName && (
                    <em>{fieldErrors.companyName.message}</em>
                  )}
                </div>
                <div
                  className={`create-user__form-group ${
                    fieldErrors.companyNumber ? 'has-error' : ''
                  } ${isAdmin ? 'hidden-field' : ''}`}
                >
                  <label>
                    <span>Organization number</span>
                    <input
                      type="text"
                      placeholder=""
                      name="companyNumber"
                      disabled={userCreated || creatingUser}
                      readOnly={true}
                      value={
                        !this.createNewAccountSelected()
                          ? account.companyNumber
                          : user.companyNumber
                      }
                    />
                  </label>
                  {fieldErrors.companyNumber && (
                    <em>{fieldErrors.companyNumber.message}</em>
                  )}
                </div>
              </>
            )}
            {this.createNewAccountSelected() && (
              <>
                <div
                  className={`create-user__form-group ${
                    fieldErrors.companyName ? 'has-error' : ''
                  } ${isAdmin ? 'hidden-field' : ''}`}
                >
                  <label>
                    <span>Company name</span>
                    {this.state.user.country !== 'Norway' && (
                      <input
                        type="txt"
                        name="companyName"
                        value={this.state.user.companyName}
                        onChange={event =>
                          this.updateUser({
                            companyName: event.target.value,
                          })
                        }
                        disabled={userCreated || creatingUser}
                      />
                    )}
                    {this.state.user.country === 'Norway' && (
                      <input
                        style={{
                          borderColor:
                            this.state.reqCompanyNameFromFetch ===
                            ReqEnum.SUCCESS
                              ? 'black'
                              : this.state.reqCompanyNameFromFetch ===
                                ReqEnum.PENDING
                              ? 'yellow'
                              : 'red',
                        }}
                        type="text"
                        placeholder=""
                        name="companyName"
                        readOnly={isCourier}
                        value={
                          this.state.reqCompanyNameFromFetch === ReqEnum.SUCCESS
                            ? this.state.companyNameFromFetch.toString()
                            : this.state.reqCompanyNameFromFetch ===
                              ReqEnum.PENDING
                            ? accountIdSelected != null &&
                              accountIdSelected >= 0
                              ? ''
                              : 'make sure the company number is written correct'
                            : `Saved: ${this.state.user.companyName}, there is a network error to www.brreg.no/`
                        }
                        onChange={event => {
                          this.setState({
                            companyNameFromFetch: event.target.value,
                          });
                          this.updateUser({
                            companyName: event.target.value,
                          });
                        }}
                        disabled={isCourier || userCreated || creatingUser}
                      />
                    )}
                  </label>

                  <ShowTestAccountIfItExists
                    isCompanyInTestAccountsList={
                      this.state.isCompanyInTestAccountsList
                    }
                    isCompanyInAccountsList={this.state.isCompanyInAccountsList}
                    isIdenticalCompanyNumber={false}
                    role={this.state.user.role}
                  />
                  {fieldErrors.companyName && (
                    <em>{fieldErrors.companyName.message}</em>
                  )}
                </div>
                <div
                  className={`create-user__form-group ${
                    fieldErrors.companyNumber ? 'has-error' : ''
                  } ${isAdmin ? 'hidden-field' : ''}`}
                >
                  <label>
                    <span>Organization number</span>
                    <input
                      type="text"
                      placeholder=""
                      name="companyNumber"
                      onChange={async event => {
                        const currentOrgNumber = event.target.value;
                        await this.getCompanyName(currentOrgNumber);

                        const companyNameToUpdate =
                          this.state.reqCompanyNameFromFetch ===
                            ReqEnum.SUCCESS &&
                          this.state.user.country === 'Norway'
                            ? this.state.companyNameFromFetch
                            : this.state.user.companyName;

                        const updatedUserFields: Partial<NewUser> = {
                          companyNumber:
                            getOrgNumberStringAsString(currentOrgNumber),
                          companyName: companyNameToUpdate,
                        };

                        this.updateUser(updatedUserFields);
                      }}
                      disabled={userCreated || creatingUser}
                      readOnly={!this.createNewAccountSelected()}
                      value={
                        accountIdSelected && !this.createNewAccountSelected()
                          ? account.companyNumber
                          : user.companyNumber
                      }
                    />
                  </label>
                  {fieldErrors.companyNumber && (
                    <em>{fieldErrors.companyNumber.message}</em>
                  )}
                </div>
                <div
                  className={classNames(
                    {
                      'hidden-field': !isSender,
                    },
                    'create-user__form-group'
                  )}
                >
                  <label>
                    <span className="create-user__form-label">
                      Brand Preset
                    </span>
                    <div className="create-user__form-radio normal">
                      <label>
                        <input
                          type="radio"
                          placeholder=""
                          name="preset-porterbuddy"
                          checked={
                            user.brandPreset === ServiceBrand.PORTERBUDDY
                          }
                          onChange={() =>
                            this.updateUser({
                              brandPreset: ServiceBrand.PORTERBUDDY,
                            })
                          }
                        />
                        Porterbuddy
                      </label>
                      <label>
                        <input
                          type="radio"
                          placeholder=""
                          name="preset-ibx-hd"
                          checked={
                            user.brandPreset === ServiceBrand.INSTABOX_HD
                          }
                          onChange={() =>
                            this.updateUser({
                              brandPreset: ServiceBrand.INSTABOX_HD,
                            })
                          }
                        />
                        Instabox HD
                      </label>
                    </div>
                  </label>
                </div>
              </>
            )}

            <div
              className={classNames(
                {
                  'has-error': !!fieldErrors.regions,
                  'hidden-field': !isAdmin && !isCourier,
                },
                'create-user__form-group'
              )}
            >
              <label>
                <span className="create-user__form-label">Regions</span>
                <div className="create-user__form-checkboxes">
                  {RegionsList.map(regionItem => (
                    <label key={regionItem}>
                      <input
                        type="checkbox"
                        placeholder=""
                        name={`regions${regionItem}`}
                        checked={user.regions.includes(regionItem)}
                        onChange={event =>
                          this.updateUser({
                            regions: toggleElement(user.regions, regionItem),
                          })
                        }
                      />
                      <span>{capitalize(regionNames[regionItem])}</span>
                    </label>
                  ))}
                </div>
              </label>
              {fieldErrors.regions && <em>{fieldErrors.regions.message}</em>}
            </div>
            <div
              className={`create-user__form-group ${
                fieldErrors.tasks ? 'has-error' : ''
              } ${!isCourier ? 'hidden-field' : ''}`}
            >
              <label>
                <span className="create-user__form-label">Task(s)</span>
                <div className="create-user__form-checkboxes">
                  <label>
                    <input
                      type="checkbox"
                      placeholder=""
                      name={`task-courier`}
                      checked={user.isCourier}
                      onChange={event =>
                        this.updateUser({
                          isCourier: !user.isCourier,
                        })
                      }
                    />
                    <span>Delivery</span>
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      placeholder=""
                      name={`task-sorter`}
                      checked={user.isSorter}
                      onChange={event =>
                        this.updateUser({
                          isSorter: !user.isSorter,
                        })
                      }
                    />
                    <span>Sorting</span>
                  </label>
                </div>
              </label>
              {fieldErrors.tasks && <em>{fieldErrors.tasks.message}</em>}
            </div>
            <div
              className={clsx(
                'create-user__form-group',
                fieldErrors.travelMode ? 'has-error' : '',
                !isCourier ? 'hidden-field' : '',
                user.isCourier ? '' : 'hidden-field'
              )}
            >
              <label style={{ margin: '12px auto' }}>
                <span>Vehicle</span>
                <div className="create-user__form-radio">
                  <label>
                    <input
                      type="radio"
                      placeholder=""
                      name="carTypeCargoBike"
                      checked={this.state.user.carType === CarType.CARGO_BIKE}
                      onChange={() =>
                        this.updateUser({
                          travelMode: CourierTravelMode.Bike,
                          carType: CarType.CARGO_BIKE,
                        })
                      }
                    />
                    <span>
                      <em style={{ whiteSpace: 'nowrap' }}>Cargo Bike</em>{' '}
                      <span className="create-user__form-icon">
                        <CargoBikeIcon />
                      </span>
                    </span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      placeholder=""
                      name="carTypeNormalBike"
                      checked={this.state.user.carType === CarType.NORMAL_BIKE}
                      onChange={() =>
                        this.updateUser({
                          travelMode: CourierTravelMode.Bike,
                          carType: CarType.NORMAL_BIKE,
                        })
                      }
                    />
                    <span>
                      <em style={{ whiteSpace: 'nowrap' }}>Normal Bike</em>{' '}
                      <span className="create-user__form-icon">
                        <BikeIcon />
                      </span>
                    </span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      placeholder=""
                      name="carTypeElectric"
                      checked={this.state.user.carType === CarType.ELECTRIC}
                      onChange={() =>
                        this.updateUser({
                          travelMode: CourierTravelMode.Car,
                          carType: CarType.ELECTRIC,
                        })
                      }
                    />
                    <span>
                      Electric{' '}
                      <span className="create-user__form-icon">
                        <FaPlug />
                      </span>
                    </span>
                  </label>
                  <label>
                    {' '}
                    <input
                      type="radio"
                      placeholder=""
                      name="carTypeHybrid"
                      checked={this.state.user.carType === CarType.HYBRID}
                      onChange={() =>
                        this.updateUser({
                          travelMode: CourierTravelMode.Car,
                          carType: CarType.HYBRID,
                        })
                      }
                    />
                    <span>
                      Hybrid{' '}
                      <span className="create-user__form-icon">
                        <FaChargingStation />
                      </span>
                    </span>
                  </label>
                  <label>
                    {' '}
                    <input
                      type="radio"
                      placeholder=""
                      name="carTypePetrol"
                      checked={this.state.user.carType === CarType.PETROL}
                      onChange={() =>
                        this.updateUser({
                          travelMode: CourierTravelMode.Car,
                          carType: CarType.PETROL,
                        })
                      }
                    />
                    <span>
                      Petrol{' '}
                      <span className="create-user__form-icon">
                        <FaGasPump />
                      </span>
                    </span>
                  </label>
                  <label>
                    {' '}
                    <input
                      type="radio"
                      placeholder=""
                      name="carTypeDiesel"
                      checked={this.state.user.carType === CarType.DIESEL}
                      onChange={() =>
                        this.updateUser({
                          travelMode: CourierTravelMode.Car,
                          carType: CarType.DIESEL,
                        })
                      }
                    />
                    <span>
                      Diesel{' '}
                      <span className="create-user__form-icon">
                        <FaOilCan />
                      </span>
                    </span>
                  </label>
                </div>
              </label>
              <label>
                <span>Plate Numbers</span>
                <input
                  type="text"
                  placeholder="e.g. DR 7123, BT 1234"
                  name="plateNumbers"
                  onChange={event => {
                    const value = event.target.value;
                    const numbers = value.split(',');
                    this.updateUser({
                      plateNumbers: numbers,
                    });
                  }}
                />
              </label>
            </div>
            <div
              className={`create-user__form-group ${
                !isAdmin ? 'hidden-field' : ''
              }`}
            >
              <label>
                <span>Limited Access</span>
                <div className="create-user__form-checkboxes">
                  <label>
                    <input
                      type="checkbox"
                      name="limitedAccess"
                      checked={user.limitedAccess}
                      onChange={event =>
                        this.updateUser({
                          limitedAccess: event.target.checked,
                        })
                      }
                    />
                  </label>
                </div>
              </label>
            </div>

            {!userCreated && (
              <div className="create-user__form-submit">
                {creatingUser && (
                  <div className="create-user__form-loader">
                    <Loader type="bounce" size="large" />
                  </div>
                )}
                <LinkButton to="/users" className="create-user__form-cancel">
                  Cancel
                </LinkButton>

                <Button
                  type="default"
                  onClick={e => this.createUser(e)}
                  title="Create"
                  disabled={creatingUser}
                />
              </div>
            )}
            {userCreated && (
              <div className="create-user__form-submit">
                <LinkButton to="/users" className="create-user__form-cancel">
                  View users
                </LinkButton>
              </div>
            )}

            {errors.length > 0 && (
              <div className="u-alert__error">
                {errors.map((error: ErrorMsg, i: number) => {
                  return (
                    <div key={`${error}-${i}`}>
                      <div style={{ fontWeight: 500 }}>{error.message}</div>
                    </div>
                  );
                })}
              </div>
            )}
            {userCreated && (
              <div className="u-alert__info">
                <p>The user has been created successfully.</p>
                <p>
                  An email has been sent to{' '}
                  <em style={{ fontWeight: 500 }}>{user.email}</em>. Click on
                  the link within it to confirm the account.
                </p>
              </div>
            )}
          </form>
        </section>
      </>
    );
  }
}
